import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiClient, apiErrorMessage } from "@/api/client";
import {
  fetchKintoneEnvironmentSwitchOperation,
  recheckKintoneEnvironmentAppReconciliation,
  syncKintoneEnvironmentAppName,
  type EnvironmentSwitchOperation,
} from "@/api/kintone-environment";
import { Button } from "@/components/ui/button";
import type { AppBindingResponse } from "@/types/api";

interface KintoneEnvironmentRepairPanelProps {
  operationId: string;
  onAppBindingChanged: () => void;
}

export function KintoneEnvironmentRepairPanel({
  operationId,
  onAppBindingChanged,
}: KintoneEnvironmentRepairPanelProps) {
  const navigate = useNavigate();
  const [operation, setOperation] = useState<EnvironmentSwitchOperation | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyAppBindingId, setBusyAppBindingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refreshOperation = useCallback(async () => {
    const current = await fetchKintoneEnvironmentSwitchOperation(operationId);
    setOperation(current);
    return current;
  }, [operationId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setMessage(null);
    void fetchKintoneEnvironmentSwitchOperation(operationId)
      .then((current) => {
        if (active) setOperation(current);
      })
      .catch((cause) => {
        if (active) {
          setMessage(apiErrorMessage(cause, "移行後のアプリ確認状態を取得できませんでした。"));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [operationId]);

  async function recheck(appBindingId: string) {
    setBusyAppBindingId(appBindingId);
    setMessage(null);
    try {
      const current = await refreshOperation();
      const reconciliation = current.reconciliations.find((item) =>
        item.appBindingId === appBindingId);
      if (!current.cutoverCommittedAt || !reconciliation) {
        throw new Error("移行後のアプリ確認状態が更新されています。再読み込みしてください。");
      }
      await recheckKintoneEnvironmentAppReconciliation(
        current.operationId,
        appBindingId,
        {
          expectedOperationRevision: current.revision,
          expectedReconciliationRevision: reconciliation.revision,
        },
        `environment-reconciliation-recheck:${crypto.randomUUID()}`,
      );
      await refreshOperation();
      onAppBindingChanged();
    } catch (cause) {
      setMessage(apiErrorMessage(cause, "アプリを再確認できませんでした。"));
    } finally {
      setBusyAppBindingId(null);
    }
  }

  async function syncName(appBindingId: string) {
    setBusyAppBindingId(appBindingId);
    setMessage(null);
    try {
      const current = await refreshOperation();
      const reconciliation = current.reconciliations.find((item) =>
        item.appBindingId === appBindingId);
      const diagnosis = current.diagnosis.find((item) => item.appBindingId === appBindingId);
      if (!current.cutoverCommittedAt || !reconciliation ||
          !diagnosis?.actualKintoneAppName ||
          diagnosis.actualKintoneAppId !== diagnosis.expectedKintoneAppId) {
        throw new Error("同期できるアプリ名の診断結果がありません。アプリを再確認してください。");
      }
      const binding = await apiClient<AppBindingResponse>(
        `/kintone-apps/${encodeURIComponent(diagnosis.expectedKintoneAppId)}/binding`,
      );
      await syncKintoneEnvironmentAppName(
        current.operationId,
        appBindingId,
        {
          expectedOperationRevision: current.revision,
          expectedReconciliationRevision: reconciliation.revision,
          expectedAppBindingRevision: binding.latestSettingsRevision,
          actualKintoneAppName: diagnosis.actualKintoneAppName,
        },
        `environment-app-name-sync:${crypto.randomUUID()}`,
      );
      await refreshOperation();
      onAppBindingChanged();
    } catch (cause) {
      setMessage(apiErrorMessage(cause, "アプリ名を同期できませんでした。"));
    } finally {
      setBusyAppBindingId(null);
    }
  }

  if (loading) {
    return (
      <section className="mb-5 flex items-center gap-2 rounded-md border px-4 py-3 text-sm text-muted-foreground" aria-live="polite">
        <Loader2 className="h-4 w-4 animate-spin" />移行後のアプリ状態を確認しています
      </section>
    );
  }

  if (!operation?.cutoverCommittedAt) {
    return message ? <RepairError message={message} /> : null;
  }

  if (operation.reconciliations.length === 0) return null;

  return (
    <section className="mb-5 max-w-5xl rounded-md border bg-muted/10 p-4" aria-labelledby="environment-repair-title">
      <div className="flex items-start gap-3">
        <Wrench className="mt-0.5 h-5 w-5 text-muted-foreground" />
        <div>
          <h2 id="environment-repair-title" className="font-semibold">移行後の登録済みアプリ</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            アプリ名が異なる場合は、移行時に確認した名前を先に同期できます。その他の設定を補正した後はアプリを再確認してください。
          </p>
        </div>
      </div>
      {message ? <div className="mt-4"><RepairError message={message} /></div> : null}
      <div className="mt-4 divide-y rounded-md border bg-background">
        {operation.reconciliations.map((reconciliation) => {
          const diagnosis = operation.diagnosis.find((item) =>
            item.appBindingId === reconciliation.appBindingId);
          const busy = busyAppBindingId === reconciliation.appBindingId;
          const canSyncName = reconciliation.reasons.includes("app_name_mismatch") &&
            diagnosis?.actualKintoneAppName != null &&
            diagnosis.actualKintoneAppId === diagnosis.expectedKintoneAppId;
          return (
            <article key={reconciliation.appBindingId} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    {reconciliation.state === "healthy"
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                    <h3 className="font-medium">{diagnosis?.expectedKintoneAppName ?? "登録済みアプリ"}</h3>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">ID: {diagnosis?.expectedKintoneAppId ?? "-"}</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {reconciliation.reasons.length === 0
                      ? "新しい環境との接続を確認できました。"
                      : reconciliation.reasons.map(repairReasonLabel).join("、")}
                  </p>
                  {canSyncName ? (
                    <p className="mt-1 text-sm">新しいアプリ名: {diagnosis.actualKintoneAppName}</p>
                  ) : null}
                  {reconciliation.downtimeStartedAt ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      停止開始: {formatTimestamp(reconciliation.downtimeStartedAt)}／停止時間: {formatDowntime(
                        reconciliation.downtimeStartedAt,
                        reconciliation.downtimeEndedAt,
                      )}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {canSyncName ? (
                    <Button variant="outline" disabled={busyAppBindingId !== null} onClick={() => void syncName(reconciliation.appBindingId)}>
                      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}アプリ名を同期
                    </Button>
                  ) : null}
                  {diagnosis?.expectedKintoneAppId ? (
                    <Button
                      variant="outline"
                      disabled={busyAppBindingId !== null}
                      onClick={() => navigate(
                        `/console/app-bindings/${encodeURIComponent(diagnosis.expectedKintoneAppId)}`,
                      )}
                    >
                      設定を補正
                    </Button>
                  ) : null}
                  <Button disabled={busyAppBindingId !== null} onClick={() => void recheck(reconciliation.appBindingId)}>
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}アプリを再確認
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RepairError({ message }: { message: string }) {
  return <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">{message}</div>;
}

function repairReasonLabel(reason: string) {
  const labels: Record<string, string> = {
    app_not_found: "移行先でアプリを確認できません",
    app_unreachable: "移行先のアプリへ接続できません",
    app_name_mismatch: "アプリ名が異なります",
    api_token_missing: "APIトークンの設定が必要です",
    api_token_unauthorized: "APIトークンを認証できません",
    api_token_forbidden: "APIトークンの権限が不足しています",
    api_token_rate_limited: "kintoneの制限により未確認です",
    api_token_timeout: "kintoneへの接続がタイムアウトしました",
    api_token_server_failure: "kintoneへの接続を確認できません",
    space_mismatch: "スペース設定が異なります",
    field_mismatch: "フィールド設定が異なります",
    field_unreadable: "フィールド設定を確認できません",
    plugin_missing: "プラグインが見つかりません",
    plugin_version_unknown: "プラグインのバージョンを確認できません",
    plugin_configuration_mismatch: "プラグイン設定の再反映が必要です",
    plugin_configuration_unreadable: "プラグイン設定を確認できません",
    target_configuration_owner_unknown: "移行先設定の所有者を確認できません",
    target_configuration_owned_by_other_binding: "移行先設定は別のアプリ連携で使用されています",
  };
  return labels[reason] ?? "確認が必要です";
}

function formatTimestamp(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "確認できません" : date.toLocaleString("ja-JP");
}

function formatDowntime(startValue: string, endValue: string | null) {
  if (endValue === null) return "継続中";
  const start = new Date(startValue).getTime();
  const end = new Date(endValue).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return "確認できません";
  const minutes = Math.ceil((end - start) / 60_000);
  return minutes < 1 ? "1分未満" : `${minutes}分`;
}
